import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft, Loader2, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

export default function CarryoverPanel() {
  const now = new Date();
  const [targetYear, setTargetYear] = useState(now.getFullYear());
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [list, setList] = useState([]);
  const [listOpen, setListOpen] = useState(false);

  const runPreview = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/annual-leave/carryovers/compute", {
        target_year: Number(targetYear), dry_run: true, overwrite,
      });
      setPreview(r.data);
      toast.success(`${r.data.with_unused} personelin devredilecek izin günü var`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Önizleme başarısız");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview) return;
    if (!window.confirm(`${targetYear} yılı için ${preview.with_unused} devir kaydı ${overwrite ? "GÜNCELLENECEK" : "oluşturulacak"}. Devam?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/annual-leave/carryovers/compute", {
        target_year: Number(targetYear), dry_run: false, overwrite,
      });
      setResult(r.data);
      toast.success(`${r.data.created} devir kaydı işlendi`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    } finally { setBusy(false); }
  };

  const loadList = async () => {
    try {
      const r = await api.get(`/annual-leave/carryovers?target_year=${targetYear}`);
      setList(r.data.rows || []);
      setListOpen(true);
    } catch { toast.error("Liste yüklenemedi"); }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Devir kaydı silinecek. Onaylıyor musunuz?")) return;
    try {
      await api.delete(`/annual-leave/carryovers/${id}`);
      toast.success("Silindi");
      loadList();
    } catch { toast.error("Silinemedi"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="carryover-panel">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
          <ArrowRightLeft size={18}/>
        </div>
        <div className="flex-1">
          <h3 className="font-display font-extrabold text-slate-900 text-base">Yıllık İzin Devir</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Her yıl sonunda kullanılmayan izin günlerini otomatik olarak bir sonraki yıla devreder.
            Kaynak = <strong>Hedef Yıl - 1</strong>. Zincirleme devir desteklenir.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3 items-end">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-1">Hedef Yıl</label>
          <input type="number" value={targetYear} min={2020} max={2040}
            onChange={(e) => { setTargetYear(e.target.value); setPreview(null); setResult(null); }}
            className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm font-mono" data-testid="carry-year"/>
        </div>
        <label className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 cursor-pointer text-xs">
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="carry-overwrite"/>
          <strong>Mevcutları üzerine yaz</strong>
        </label>
        <button onClick={runPreview} disabled={busy}
          data-testid="carry-preview-btn"
          className="inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin"/> : <ArrowRightLeft size={13}/>} Önizle
        </button>
        <button onClick={loadList}
          data-testid="carry-list-btn"
          className="inline-flex items-center justify-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-900 text-xs font-semibold px-3 py-1.5 rounded-md">
          Mevcut Devirleri Göster
        </button>
      </div>

      {preview && (
        <div className="border border-slate-200 rounded-md overflow-hidden mb-3">
          <div className="bg-slate-100 px-3 py-1.5 text-xs font-bold flex items-center gap-2">
            <span>Önizleme: {preview.source_year} → {preview.target_year}</span>
            <span className="text-emerald-700">· {preview.with_unused} personelin devredilecek günü var</span>
            {preview.with_unused > 0 && !result && (
              <button onClick={apply} disabled={busy}
                data-testid="carry-apply-btn"
                className="ml-auto inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold px-2.5 py-1 rounded">
                <CheckCircle2 size={11}/> Devretme Uygula
              </button>
            )}
            {result && (
              <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700">
                <CheckCircle2 size={12}/> {result.created} kayıt işlendi
              </span>
            )}
          </div>
          <div className="max-h-56 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left">
                  <th className="px-2 py-1 font-bold">Personel</th>
                  <th className="px-2 py-1 font-bold text-right">Madde 53</th>
                  <th className="px-2 py-1 font-bold text-right">Önceki Devir</th>
                  <th className="px-2 py-1 font-bold text-right">Kullanılan</th>
                  <th className="px-2 py-1 font-bold text-right">Devredilecek</th>
                  <th className="px-2 py-1 font-bold">Durum</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {preview.rows.filter((r) => r.status !== "zero").map((r, i) => (
                  <tr key={i} data-testid={`carry-row-${i}`}>
                    <td className="px-2 py-1">
                      <div className="font-semibold">{r.ad_soyad}</div>
                      <div className="text-[10px] text-slate-500">{r.sicil_no}</div>
                    </td>
                    <td className="px-2 py-1 text-right font-mono text-slate-700">{r.entitled_cumulative}</td>
                    <td className="px-2 py-1 text-right font-mono text-slate-700">{r.carryover_prev}</td>
                    <td className="px-2 py-1 text-right font-mono text-amber-700">{r.used_cumulative}</td>
                    <td className="px-2 py-1 text-right font-mono font-bold text-emerald-700">{r.unused}g</td>
                    <td className="px-2 py-1 text-[10px] font-bold">
                      {r.status === "would_create" && <span className="text-slate-600">Oluşturulacak</span>}
                      {r.status === "created"     && <span className="text-emerald-700">Oluşturuldu</span>}
                      {r.status === "overwritten" && <span className="text-indigo-700">Güncellendi</span>}
                      {r.status === "already_exists" && <span className="text-amber-700">Mevcut ({r.carried}g)</span>}
                    </td>
                  </tr>
                ))}
                {preview.rows.filter((r) => r.status !== "zero").length === 0 && (
                  <tr><td colSpan={6} className="text-center text-slate-400 py-3 flex items-center justify-center gap-1"><AlertTriangle size={11}/> Devredilecek izin günü bulunamadı</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {listOpen && (
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <div className="bg-emerald-50 border-b border-emerald-200 px-3 py-1.5 text-xs font-bold text-emerald-900">
            {targetYear} yılı için {list.length} devir kaydı
          </div>
          <div className="max-h-56 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left">
                  <th className="px-2 py-1 font-bold">Personel</th>
                  <th className="px-2 py-1 font-bold">Kaynak → Hedef</th>
                  <th className="px-2 py-1 font-bold text-right">Gün</th>
                  <th className="px-2 py-1 font-bold">Açıklama</th>
                  <th className="px-2 py-1"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {list.map((r) => (
                  <tr key={r.id}>
                    <td className="px-2 py-1 font-mono">{r.personnel_id.slice(0,8)}</td>
                    <td className="px-2 py-1 font-mono">{r.source_year} → {r.target_year}</td>
                    <td className="px-2 py-1 text-right font-bold text-emerald-700">{r.gun_sayisi}g</td>
                    <td className="px-2 py-1 text-[10px] text-slate-600">
                      {r.aciklama}
                      {r.auto_generated && <span className="ml-1 text-[9px] bg-slate-100 border border-slate-200 px-1 rounded">OTO</span>}
                    </td>
                    <td className="px-2 py-1 text-right">
                      <button onClick={() => deleteOne(r.id)} className="text-rose-600 hover:text-rose-800" title="Sil">
                        <Trash2 size={11}/>
                      </button>
                    </td>
                  </tr>
                ))}
                {list.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-3">Kayıt yok</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
